/**
 * FNV-1a 32-bit hash — converts an arbitrary string to a u32 seed.
 * Deterministic, dependency-free, suitable for PRNG seeding.
 */
export function stringToSeed(str: string): number {
  let hash = 0x811c9dc5 // FNV offset basis (32-bit)
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i)
    // Multiply by FNV prime (32-bit) with unsigned wrap-around
    hash = Math.imul(hash, 0x01000193)
  }
  // Force unsigned 32-bit integer
  return hash >>> 0
}

/**
 * mulberry32 PRNG — returns a function that yields the next pseudo-random
 * float in [0, 1) on each call. Fast, high-quality for non-cryptographic use.
 */
function mulberry32(seed: number): () => number {
  let s = seed
  return () => {
    s |= 0
    s = (s + 0x6d2b79f5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Returns a new array containing the elements of `arr` in a deterministic
 * pseudo-random order derived from `seed` (Fisher-Yates shuffle).
 * The original array is not mutated.
 */
export function seededShuffle<T>(arr: readonly T[], seed: number): T[] {
  const result = arr.slice()
  const rand = mulberry32(seed)
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    const tmp = result[i]!
    result[i] = result[j]!
    result[j] = tmp
  }
  return result
}
